import { useAuthedFetch } from "@/lib/useAuthedFetch";
import { APP_TO_BUILD } from "../../appsConfig";
import type {
  ReferralAppType,
  ReferralAttributionSource,
  ReferralOffer,
  ReferralRedeemFailReason,
  ReferralTrialRecord,
} from "@petergok/blue-collar-shared";
import type { AiChatUsage } from "@/types/aiChatUsage";
import {
  AI_CHAT_CONTRACT_HEADER,
  AI_CHAT_CONTRACT_VERSION,
} from "@petergok/blue-collar-shared";
const f = useAuthedFetch(APP_TO_BUILD);
// --- practiceDays (doc: practiceDays/{userId})
export const getPracticeDays = async () =>
  (await f)<{ latest?: string }>("/v1/practice-days", { method: "GET" });

export const setPracticeDays = async (latest: string) =>
  (await f)("/v1/practice-days", { method: "PUT", json: { latest } });

export const getCodebookDays = async () =>
  (await f)<{ latest?: string }>("/v1/codebook-days", { method: "GET" });

export const setCodebookDays = async (latest: string) =>
  (await f)("/v1/codebook-days", { method: "PUT", json: { latest } });

// --- devices (doc: devices/{userId})
export const getDevice = async () => (await f)("/v1/device", { method: "GET" });

export const resetDeviceProfileToNulls = async () =>
  (await f)("/v1/device", {
    method: "PUT",
    json: {
      profile_email: null,
      profile_school: null,
      profile_name: null,
      profile_exam_date: null,
      user_id: null,
      knowledge_level: null,
      codeRedeemed: null,
    },
  });

export const updateDeviceProfile = async (payload: {
  profile_email: string;
  profile_school?: string | null;
  referral_source?: string | null;
  exam_date_selection?: string | null;
  profile_name?: string | null;
  selected_state_code_at_profile_create?: string | null;
  knowledge_level?: string | null;
  current_state_code?: string | null;
}) => (await f)("/v1/device", { method: "PUT", json: payload });

// --- profile (doc: profiles/{userId})
export const getProfile = async () =>
  (await f)("/v1/profile", { method: "GET" });

export const upsertProfile = async (payload: {
  profile_email?: string | null;
  profile_school?: string | null;
  referral_source?: string | null;
  exam_date_selection?: string | null;
  scheduled_exam_date?: string | null;
  has_booked_exam?: boolean | null;
  profile_name?: string | null;
  selected_state_code_at_profile_create?: string;
  knowledge_level?: string | number | null;
  current_state_code?: string;
  codebook_year?: number | null;
  code_redeemed?: string | null;
  force_purchase_state?: string | null;
  opened_app?: boolean;
  latest_version?: string;
  last_updated?: string | Date;
  new_auth_user?: boolean;
  authentication_id?: string;
  code_redeemed_list?: Array<string>;
  sign_up_attribution?: string;
  have_shown_coachmark?: boolean;
  has_seen_trial_offer?: boolean;
}) => (await f)("/v1/profile", { method: "POST", json: payload });

// --- purchaseState (doc: purchaseState/{userId})
export const upsertPurchaseState = async (payload: {
  purchaseAccess?: any;
  purchasedProducts?: Array<{ sku: string; humanReadable?: string }>;
  forcePurchaseAccess?: {
    aiAccess?: boolean;
    studyAccess?: boolean;
    allLevelStudyAccess?: boolean;
  };
}) => (await f)("/v1/purchase-state", { method: "POST", json: payload });

export const getPurchaseState = async () =>
  (await f)("/v1/purchase-state", { method: "GET" });

export const markDeviceOpened = async (payload: {
  opened_app: boolean;
  latest_version: string;
}) =>
  (await f)("/v1/device", {
    method: "PUT",
    json: { ...payload, last_updated_server: true },
  });

export const setPurchaseState = async (payload: {
  paid: boolean;
  purchase_state: string;
}) => (await f)("/v1/device", { method: "PUT", json: payload });

export const setCodeRedeemedOnDevice = async (code: string) =>
  (await f)("/v1/device", { method: "PUT", json: { codeRedeemed: code } });

// --- accessCodes (doc: accessCodes/{code})
export const getAccessCode = async (code: string) =>
  (await f)(`/v1/access-codes/${encodeURIComponent(code)}`, { method: "GET" });

/**
 * Timed out on purpose: the redemption dialog is deliberately non-dismissable while this
 * runs (no close button, outside-click prevented, Escape ignored), so a hung request left
 * an org/school learner staring at a spinner they could only escape by reloading the
 * page — on their very first experience of the product.
 */
export const redeemAccessCode = async (code: string) =>
  (await f)(`/v1/access-codes/${encodeURIComponent(code)}/redeem`, {
    method: "POST",
    timeoutMs: 20_000,
  });

// --- questionFrequencies (doc: questionFrequencies/{userId})
export const getQuestionFrequencies = async () =>
  (await f)<{ latest?: string }>("/v1/question-frequencies", { method: "GET" });

export const setQuestionFrequencies = async (
  latest: string,
  opts?: { keepalive?: boolean },
) =>
  (await f)("/v1/question-frequencies", {
    method: "PUT",
    json: { latest },
    keepalive: opts?.keepalive,
  });

// --- tests (doc: tests/{userId})
export const getTests = async () =>
  (await f)<{ latest?: string }>("/v1/tests", { method: "GET" });

export const setTests = async (latest: string) =>
  (await f)("/v1/tests", { method: "PUT", json: { latest } });

export const getDeletedTestIds = async () =>
  (await f)<{ deletedTestIds: string[] }>("/v1/tests/deleted", {
    method: "GET",
  });

export const deleteTest = async (testId: string) =>
  (await f)("/v1/tests/" + encodeURIComponent(testId), { method: "DELETE" });

export const createReportedQuestion = async (payload: {
  question: { question_id: string; [k: string]: any };
  error?: string;
  email?: string;
}) => (await f)("/v1/tests/report-question", { method: "POST", json: payload });

// --- Question Reports (AI-triaged system) ---

export const submitReport = async (payload: {
  question: Record<string, any>;
  error: string;
  code_used?: string;
  email?: string;
}) =>
  (await f)<{
    reportId: string;
    status: "auto_respond" | "sme_queue";
    aiResponse?: string;
  }>("/v1/reports", { method: "POST", json: payload });

export const resolveReport = async (reportId: string) =>
  (await f)(`/v1/reports/${encodeURIComponent(reportId)}/resolve`, {
    method: "POST",
  });

export const escalateReport = async (reportId: string) =>
  (await f)(`/v1/reports/${encodeURIComponent(reportId)}/escalate`, {
    method: "POST",
  });

export const getSubsectionNames = async (): Promise<{ value?: string }> =>
  (await f)("/v1/tests/subsection-names", { method: "GET" });

// --- aiChatHistory (doc: aiChatHistory/{chatId})
export const upsertAiChatMessages = async (chatId: string, messages: any[]) =>
  (await f)(`/v1/ai-chat/${encodeURIComponent(chatId)}/messages`, {
    method: "PUT",
    json: { messages },
  });

export const createAiChat = async (payload: {
  chatId: string;
  user: any;
  topic: string;
  lessonId: string;
  createdAtISO: string;
  expiresAtISO: string; // e.g. now+3d
}) =>
  (await f)("/v1/ai-chat", {
    method: "POST",
    headers: { [AI_CHAT_CONTRACT_HEADER]: AI_CHAT_CONTRACT_VERSION },
    json: payload,
  });

export const createAiFeedback = async (payload: {
  isPositive: boolean;
  feedbackText: string;
  chatId: string;
  lessonId?: string | null;
  messageIndex: number;
  messageContent: string;
  // Phase 2 R3: extra context so a downvote can seed the SME review queue.
  precedingStudentMessage?: string;
  questionId?: string | null;
  app?: string;
}) => (await f)("/v1/ai-chat/feedback", { method: "POST", json: payload });

export const getAiChatLimit = async () =>
  (await f)<AiChatUsage>(`/v1/ai-chat/limits`, { method: "GET" });

export const upsertAiChatLimit = async (payload: AiChatUsage) =>
  (await f)(`/v1/ai-chat/limits`, { method: "POST", json: payload });

// --- AI chat question tracking
export type QuestionResponse = {
  questionId: string;
  question: string;
  answers: string[];
  correctAnswer: string;
  hint: string;
  exhausted: boolean;
};

export const getNextQuestion = async (chatId: string, lessonId: string) =>
  (await f)<QuestionResponse>(
    `/v1/ai-chat/${encodeURIComponent(chatId)}/next-question`,
    { method: "POST", json: { lessonId } },
  );

export const getCurrentQuestion = async (chatId: string) =>
  (await f)<QuestionResponse>(
    `/v1/ai-chat/${encodeURIComponent(chatId)}/current-question`,
    { method: "GET" },
  );

// --- global/config reads
export const getForceUpdateVersion = async () =>
  (await f)("/v1/config/force-update", { method: "GET" });

export const getShowRestore = async () =>
  (await f)("/v1/config/show-restore", { method: "GET" });

export const getHideAccessCodes = async () =>
  (await f)("/v1/config/hide-access-codes", { method: "GET" });

export const getInAppBannerInfo = async () =>
  (await f)("/v1/config/in-app-banner-info", { method: "GET" });

export const getStateInfo = async () =>
  (await f)("/v1/config/state-info", { method: "GET" }, true);

export const getCurriculum = async () =>
  (await f)("/v1/config/curriculum", { method: "GET" }, true);

export const getTypesMinMax = async () =>
  (await f)("/v1/config/types-min-max", { method: "GET" }, true);

export const getAllQuestions = async () =>
  (await f)("/v1/content/all-questions", { method: "GET" }, true);

export const getDeletedQuestions = async () =>
  (await f)("/v1/content/deleted-questions", { method: "GET" });

export const getProducts = async () =>
  (await f)("/v1/stripe/products", { method: "GET" });

export const createCheckoutSession = async (payload: {
  priceId: string;
  successUrl: string;
  cancelUrl: string;
}) =>
  (await f)("/v1/stripe/create-checkout-session", {
    method: "POST",
    json: payload,
  });

export const checkPurchaseState = async () =>
  (await f)("/v1/purchase-state/check-purchase-state", { method: "POST" });

export const createPortalSession = async (payload: { returnUrl: string }) =>
  (await f)("/v1/stripe/create-portal-session", {
    method: "POST",
    json: payload,
  });

// --- generic query
export const getCollectionAsObject = async (params: {
  collectionName: string;
  orderKey: string;
  direction?: "asc" | "desc";
  filterField?: string;
  filterValue?: any;
}) =>
  (await f)("/v1/query/collection-as-object", { method: "POST", json: params });

// --- referral (refer-a-friend)
export const getReferralLink = async () =>
  (await f)<{
    code: string;
    url: string;
    redemptionCount: number;
    cap: number;
  }>("/v1/referral/link", { method: "GET" });

// Resolves with the parsed body for BOTH 2xx and typed 4xx outcomes — a 4xx
// `{ ok: false, reason }` is a final answer, not an error. Throws only on
// 5xx/network failures, so callers should retry on throw only.
export const redeemReferral = async (payload: {
  code: string;
  attributionSource: ReferralAttributionSource;
}) =>
  (await f)<
    | { ok: true; offer: ReferralOffer }
    | { ok: false; reason: ReferralRedeemFailReason }
  >("/v1/referral/redeem", {
    method: "POST",
    json: payload,
    resolveClientErrors: true,
  });

export const claimReferralTrial = async () =>
  (await f)<{ ok: true; trial?: ReferralTrialRecord }>(
    "/v1/referral/claim-trial",
    { method: "POST", json: {} },
  );

export const dismissReferralOffer = async () =>
  (await f)<{ ok: true }>("/v1/referral/claim-trial", {
    method: "POST",
    json: { action: "dismiss" },
  });

export const markReferralExpiryPopupSeen = async () =>
  (await f)<{ ok: true }>("/v1/referral/claim-trial", {
    method: "POST",
    json: { action: "expiry-popup-seen" },
  });

// Public endpoint — no auth header (same noAuth flag as getStateInfo).
export const getReferralPreview = async (code: string) =>
  (await f)<{
    valid: boolean;
    capReached?: boolean;
    appType?: ReferralAppType;
    referrerFirstName?: string;
  }>(
    `/v1/referral/preview/${encodeURIComponent(code)}`,
    { method: "GET" },
    true,
  );

export interface CheckSessionResponse {
  isValid: boolean;
}

/**
 * Typed on purpose. The caller used to cast this to `any` and branch on
 * `!result.isValid`, so ANY 200 whose body lacked the field — a shape change, `{}`, a
 * `{ ok, data }` envelope — read as falsy and signed the user out. A response type makes
 * that regression visible at compile time.
 */
export const checkFirebaseSession = async () =>
  (await f)<Partial<CheckSessionResponse>>("/v1/clerk/check-session", {
    method: "GET",
    // Short and interactive: this runs on a timer, so a hung request would otherwise
    // stack up behind the in-flight guard and stall session checks indefinitely.
    timeoutMs: 10_000,
  });

export const registerSession = async () =>
  (await f)("/v1/clerk/register-session", { method: "POST" });
